import React, { useState, useEffect } from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { roleOptions } from '../../static/common';
import Papa from 'papaparse';

const BulkInviteUser = ({ handleSubmit, loading, inviteResults = null }) => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [showResultsOnly, setShowResultsOnly] = useState(false);

  useEffect(() => {
    setShowResultsOnly(!!inviteResults);
  }, [inviteResults]);

  const handleFileUpload = (e) => {
    setError('');
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: 'UTF-8',
      complete: function (results) {
        const data = results.data;
        if (!data || data.length === 0) {
          setError('The uploaded file is empty. Please upload a valid CSV file.');
          setUsers([]);
          setFileName('');
          return;
        }
        const parsedUsers = [];
        const seenEmails = new Set();
        let hasDuplicates = false;

        for (const row of data) {
          const firstName = row['first name']?.trim();
          const email = row['email']?.trim().toLowerCase();
          const inputRole = row['role']?.trim().toLowerCase();
          const matchedRole = roleOptions.find(
            (r) => r.label.toLowerCase() === inputRole
          );

          if (!firstName || !email || !matchedRole) {
            setError(
              'The uploaded file contains some invalid or missing values. Please check and try again.'
            );
            setUsers([]);
            setFileName('');
            return;
          }

          if (seenEmails.has(email)) {
            hasDuplicates = true;
            continue;
          }

          seenEmails.add(email);

          parsedUsers.push({
            firstName,
            lastName: row['last name']?.trim() || '',
            email,
            role: matchedRole.value,
          });
        }

        if (parsedUsers.length > 10) {
          setError('You can only invite up to 10 users at a time.');
          setUsers([]);
          setFileName('');
          return;
        }
        if (hasDuplicates) {
          setError('Duplicate emails found in the uploaded file. Only unique emails were included.');
        } else {
          setError('');
        }
        setUsers(parsedUsers);
        setFileName(file.name);
      },
    });
  };

  const handleSampleDownload = () => {
    const sample = [
      ['first name', 'last name', 'email', 'role'],
      ['John', 'Doe', 'john@example.com', 'Team Member'],
      ['Jane', 'Smith', 'jane@example.com', 'Executive Manager'],
      ['Bob', 'Johnson', 'bob@example.com', 'Organization Manager'],
    ];
    const csvContent = sample.map((e) => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'sample_bulk_invite.csv';
    link.click();
  };

  const handleTryAgain = () => {
    setUsers([]);
    setError('');
    setFileName('');
    setShowResultsOnly(false);
  };

  const handleSubmitForm = () => {
    handleSubmit(users);
    setShowResultsOnly(false);
    setError('');
    setFileName('');
    setUsers([]);
  };

  return (
    <Grid container spacing={2}>
      {showResultsOnly ? (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="success.main">
              {inviteResults?.invited.length || 0} user(s) have been invited successfully
            </Typography>

            {inviteResults?.failed.length > 0 && (
              <>
                <Typography
                  variant="subtitle1"
                  color="error.main"
                  sx={{ mt: 1 }}>
                  Some users could not be invited. You can see the details
                  below.
                </Typography>
                <ul style={{ paddingLeft: '1.5rem', marginTop: 0 }}>
                  {inviteResults.failed.map((fail, index) => (
                    <li key={index}>
                      <Typography variant="body2" color="error.main">
                        {fail.email}: {fail.reason}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleTryAgain} fullWidth>
              {inviteResults.failed.length > 0 ? 'Try Again' : 'Invite More Users'}
            </Button>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12}>
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <Button onClick={handleSampleDownload}>
                Download Sample CSV
              </Button>
            </Grid>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}>
              {fileName ? 'Replace File' : 'Upload CSV File'}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
          </Grid>

          {fileName && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Uploaded file: <strong>{fileName}</strong>
              </Typography>
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={users.length === 0 || loading}
              onClick={handleSubmitForm}
              fullWidth>
              Invite Users
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default BulkInviteUser;
